import React, { useState, useEffect } from "react";
import { screenText } from "../utils/screen";

// Web twin of the phone's MealBreakdownModal (front-end/src/components/
// Mealbreakdownmodal.js): the same three parts, the same chip options, the same
// "or type your own" box, and the same saved shape ({ protein, starch, vegetable },
// "None" for a part left empty). Shell mirrors ScheduleModalWeb.
const OPTIONS = {
  Protein: ["Fish", "Chicken", "Turkey", "Beef", "Pork", "Eggs", "Tofu", "Beans", "Lentils", "None"],
  Starch: ["Rice", "Potato", "Bread", "Pasta", "Corn", "Plantain", "Beans", "None"],
  Vegetable: ["Broccoli", "Spinach", "Salad", "Carrots", "Peppers", "Cabbage", "Green beans", "None"],
};

function ChipSelector({ label, value, onChange }) {
  const options = OPTIONS[label];
  const typed = value && !options.includes(value) ? value : "";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ fontWeight: 700 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {options.map((opt) => (
          <button type="button" key={opt} onClick={() => onChange(opt)}
            style={{ padding: "6px 12px", borderRadius: "16px", cursor: "pointer", fontSize: "13px",
              border: value === opt ? "1.5px solid blue" : "1px solid #ccd",
              background: value === opt ? "#e8efff" : "#fff", color: value === opt ? "blue" : "#333" }}>
            {opt}
          </button>
        ))}
      </div>
      <input type="text" value={typed} onChange={(e) => onChange(e.target.value)}
        placeholder={`Or type your own ${label.toLowerCase()}…`}
        style={{ padding: "10px", background: "#f0f8ff", border: "1px solid #cce", borderRadius: "6px" }} />
    </div>
  );
}

export default function MealBreakdownWeb({ open, mealDescription = "", onSave, onClose }) {
  const [protein, setProtein] = useState("");
  const [starch, setStarch] = useState("");
  const [vegetable, setVegetable] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setProtein(""); setStarch(""); setVegetable(""); setError(""); } }, [open]);
  if (!open) return null;

  async function handleSave(e) {
    e.preventDefault();
    if (!(protein || starch || vegetable)) return;
    // Typed parts get the same prefilter as every other box.
    for (const [label, value, options] of [["protein", protein, OPTIONS.Protein], ["starch", starch, OPTIONS.Starch], ["vegetable", vegetable, OPTIONS.Vegetable]]) {
      if (value && !options.includes(value)) {
        const r = await screenText(value, label);
        if (!r.ok) { setError(r.message); return; }
      }
    }
    setSaving(true);
    try {
      await onSave({ protein: protein.trim() || "None", starch: starch.trim() || "None", vegetable: vegetable.trim() || "None" });
    } finally { setSaving(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9750, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
      onClick={onClose}>
      <form onSubmit={handleSave}
        style={{ background: "#fff", borderRadius: "14px", width: "min(520px,100%)", maxHeight: "90vh", overflow: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: "14px", position: "relative" }}
        onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose}
          style={{ position: "absolute", top: "12px", right: "12px", border: "none", background: "transparent", fontSize: "20px", cursor: "pointer", color: "#666" }}>✕</button>
        <div style={{ fontWeight: 800, fontSize: "17px" }}>Break down your meal</div>
        <p style={{ color: "#333", fontSize: "14px", margin: "0 24px 0 0", lineHeight: 1.5 }}>
          Thanks. Now break <b>{mealDescription || "your meal"}</b> into its main parts: protein, starch, vegetable.
          <br /><span style={{ color: "#777" }}>Example: fish, rice, broccoli</span>
        </p>
        <ChipSelector label="Protein" value={protein} onChange={(v) => { setProtein(v); setError(""); }} />
        <ChipSelector label="Starch" value={starch} onChange={(v) => { setStarch(v); setError(""); }} />
        <ChipSelector label="Vegetable" value={vegetable} onChange={(v) => { setVegetable(v); setError(""); }} />
        {error && <div role="alert" style={{ color: "#c00", fontSize: "13px" }}>{error}</div>}
        <button type="submit" disabled={saving || !(protein || starch || vegetable)}
          style={{ alignSelf: "center", padding: "12px 32px", background: "blue", color: "#fff", border: "none", borderRadius: "10px", fontWeight: 600, cursor: saving ? "wait" : "pointer", opacity: (protein || starch || vegetable) ? 1 : 0.5 }}>
          {saving ? "Saving…" : "Save meal to food ledger"}
        </button>
      </form>
    </div>
  );
}
